import type { ROIDashboardProps } from './types';

export const mockNoHistory: ROIDashboardProps = {
  mode: 'prospect',
  dataMaturity: {
    hasAuditedOperations: false,
    auditedOperationsCount: 0,
    typologyRelevance: 'none',
    hasAdoptionData: false,
    capitalizationMonths: 0,
  },
  fiabilisation: {
    capitalizedPrices: 0,
    analyzedOffers: 0,
    documentedCostEstimates: 0,
  },
  insights: [
    {
      type: 'fiabilite',
      subtype: 'structuration',
      title: 'Structuration automatique des offres',
      headlineValue: '85%',
      caption: 'de temps économisé sur le pointage manuel DPGF/BPU',
      context: 'benchmark Sillant vs Excel traditionnel',
      miniViz: {
        type: 'bars',
        unit: 'h',
        data: [
          { label: 'Excel', value: 12 },
          { label: 'Sillant', value: 2, highlight: true },
        ],
      },
      footer: { label: 'GAIN PRODUCTIVITÉ', value: 'Équivalent 2 jours/chiffrage', highlightColor: 'violet' },
    },
    {
      type: 'fiabilite',
      subtype: 'oab-detection',
      title: 'Détection automatique des OAB',
      headlineValue: '100%',
      caption: 'des offres analysées avec détection statistique',
      context: 'méthode double moyenne 20%',
      miniViz: {
        type: 'scatter',
        unit: 'k€',
        data: [
          { label: 'Off1', value: 450 },
          { label: 'Off2', value: 480 },
          { label: 'Off3', value: 290, highlight: true },
          { label: 'Off4', value: 470 },
          { label: 'Off5', value: 510 },
        ],
      },
      footer: { label: 'SÉCURITÉ JURIDIQUE', value: 'Traçabilité complète OAB', highlightColor: 'violet' },
    },
    {
      type: 'fiabilite',
      subtype: 'capitalisation',
      title: 'Capitalisation continue',
      headlineValue: '+0',
      caption: 'prix capitalisés aujourd’hui',
      context: 'votre référentiel démarre de zéro',
      miniViz: {
        type: 'progression',
        unit: 'prix',
        data: [
          { label: 'M0', value: 0 },
          { label: 'M3', value: 120 },
          { label: 'M6', value: 340 },
          { label: 'M12', value: 890 },
        ],
      },
      footer: { label: 'PROJECTION 12 MOIS', value: '~890 prix propriétaires', highlightColor: 'violet' },
    },
  ],
  capitalization: {
    currentPriceCount: 0,
    monthlyProgression: [],
  },
  scope: {
    dceCount: 0,
    periodStart: '—',
    periodEnd: '—',
    totalMarketValue: 0,
    lineItemsAnalyzed: 0,
  },
};

export const mockLowHistory: ROIDashboardProps = {
  mode: 'prospect',
  dataMaturity: {
    hasAuditedOperations: true,
    auditedOperationsCount: 2,
    typologyRelevance: 'medium',
    hasAdoptionData: false,
    capitalizationMonths: 2,
  },
  fiabilisation: {
    capitalizedPrices: 180,
    analyzedOffers: 14,
    documentedCostEstimates: 2,
  },
  economy: {
    minAnnual: 400_000,
    maxAnnual: 900_000,
    operationsAudited: 2,
    operationsPerYear: 12,
    confidenceLevel: 'preliminary',
  },
  insights: [
    {
      type: 'fiabilite',
      subtype: 'structuration',
      title: 'Structuration automatique des offres',
      headlineValue: '14',
      caption: 'offres structurées sur vos 2 opérations auditées',
      context: '~22h de pointage manuel évitées',
      miniViz: {
        type: 'bars',
        unit: 'offres',
        data: [
          { label: 'Opé A', value: 6 },
          { label: 'Opé B', value: 8, highlight: true },
        ],
      },
      footer: { label: 'PATRIMOINE STRUCTURÉ', value: '+180 prix propriétaires', highlightColor: 'violet' },
    },
    {
      type: 'fiabilite',
      subtype: 'capitalisation',
      title: 'Démarrage de votre référentiel',
      headlineValue: '180',
      caption: 'prix documentés et structurés',
      context: 'premier socle de votre bible propriétaire',
      miniViz: {
        type: 'progression',
        unit: 'prix',
        data: [
          { label: 'M0', value: 0 },
          { label: 'M1', value: 80 },
          { label: 'M2', value: 180 },
        ],
      },
      footer: { label: 'TRAJECTOIRE', value: '~900 prix projetés à 12 mois', highlightColor: 'violet' },
    },
    {
      type: 'mixte',
      subtype: 'dispersion',
      title: 'Premier signal de dispersion',
      headlineValue: '34%',
      caption: 'd’écart observé sur postes récurrents',
      context: 'signal à confirmer avec plus d’opérations',
      miniViz: {
        type: 'bars',
        unit: '€',
        data: [
          { label: 'Opé A', value: 420 },
          { label: 'Opé B', value: 560, highlight: true },
        ],
      },
      footer: { label: 'POTENTIEL À CONFIRMER', value: '~30 k€/opération (préliminaire)', highlightColor: 'both' },
    },
  ],
  capitalization: {
    currentPriceCount: 180,
    monthlyProgression: [
      { month: '2026-02', priceCount: 0 },
      { month: '2026-03', priceCount: 80 },
      { month: '2026-04', priceCount: 180 },
    ],
  },
  scope: {
    dceCount: 2,
    periodStart: '2025-09',
    periodEnd: '2026-02',
    totalMarketValue: 4_200_000,
    lineItemsAnalyzed: 387,
  },
};

export const mockRichHistory: ROIDashboardProps = {
  mode: 'client',
  dataMaturity: {
    hasAuditedOperations: true,
    auditedOperationsCount: 5,
    typologyRelevance: 'high',
    hasAdoptionData: true,
    capitalizationMonths: 6,
  },
  fiabilisation: {
    capitalizedPrices: 1247,
    analyzedOffers: 98,
    documentedCostEstimates: 12,
  },
  economy: {
    minAnnual: 1_800_000,
    maxAnnual: 2_400_000,
    operationsAudited: 5,
    operationsPerYear: 20,
    confidenceLevel: 'standard',
  },
  insights: [
    {
      type: 'fiabilite',
      subtype: 'capitalisation',
      title: 'Votre bible s’enrichit',
      headlineValue: '+340',
      caption: 'prix ajoutés ce trimestre',
      context: '12 chiffrages et 98 offres analysés',
      miniViz: {
        type: 'progression',
        unit: 'prix',
        data: [
          { label: 'M1', value: 640 },
          { label: 'M2', value: 780 },
          { label: 'M3', value: 907 },
          { label: 'M4', value: 1024 },
          { label: 'M5', value: 1160 },
          { label: 'M6', value: 1247 },
        ],
      },
      footer: { label: 'COUVERTURE TYPOLOGIES', value: '3 typologies sur 4 actives', highlightColor: 'violet' },
    },
    {
      type: 'economie',
      subtype: 'dispersion',
      title: 'Dispersion des prix unitaires',
      headlineValue: '89%',
      caption: 'd’écart max sur bloc-porte palière',
      context: 'entre vos 5 opérations auditées',
      miniViz: {
        type: 'bars',
        unit: '€',
        data: [
          { label: 'Opé A', value: 380 },
          { label: 'Opé B', value: 520 },
          { label: 'Opé C', value: 720, highlight: true },
          { label: 'Opé D', value: 460 },
          { label: 'Opé E', value: 510 },
        ],
      },
      footer: { label: 'POTENTIEL CAPTURABLE', value: '45 – 80 k€ / opération', highlightColor: 'peach' },
    },
    {
      type: 'mixte',
      subtype: 'bias',
      title: 'Biais récurrent lot CVC',
      headlineValue: '12%',
      caption: 'de sous-estimation sur le lot CVC',
      context: 'observé sur 4 opérations sur 5',
      miniViz: {
        type: 'comparison',
        unit: 'k€',
        data: [
          { label: 'CVC A', value: -140 },
          { label: 'CVC B', value: -95 },
          { label: 'CVC C', value: -220, highlight: true },
          { label: 'CVC D', value: -85 },
        ],
      },
      footer: { label: 'FIABILISE + NÉGOCIE', value: '380 – 540 k€ sur 12 mois', highlightColor: 'both' },
    },
  ],
  adoption: {
    pricesConsulted: 284,
    anomaliesIdentified: 47,
    costEstimatesProduced: 12,
    activeUsers: 8,
    weeklyActivity: [
      { week: '2026-W05', pricesConsulted: 12 },
      { week: '2026-W06', pricesConsulted: 18 },
      { week: '2026-W07', pricesConsulted: 24 },
      { week: '2026-W08', pricesConsulted: 31 },
      { week: '2026-W09', pricesConsulted: 28 },
      { week: '2026-W10', pricesConsulted: 35 },
      { week: '2026-W11', pricesConsulted: 42 },
      { week: '2026-W12', pricesConsulted: 38 },
      { week: '2026-W13', pricesConsulted: 29 },
      { week: '2026-W14', pricesConsulted: 27 },
    ],
  },
  capitalization: {
    currentPriceCount: 1247,
    monthlyProgression: [
      { month: '2025-11', priceCount: 640 },
      { month: '2025-12', priceCount: 780 },
      { month: '2026-01', priceCount: 907 },
      { month: '2026-02', priceCount: 1024 },
      { month: '2026-03', priceCount: 1160 },
      { month: '2026-04', priceCount: 1247 },
    ],
  },
  scope: {
    dceCount: 5,
    periodStart: '2024-03',
    periodEnd: '2026-02',
    totalMarketValue: 18_400_000,
    lineItemsAnalyzed: 1247,
  },
};

export const mockMatureClient: ROIDashboardProps = {
  mode: 'client',
  dataMaturity: {
    hasAuditedOperations: true,
    auditedOperationsCount: 18,
    typologyRelevance: 'high',
    hasAdoptionData: true,
    capitalizationMonths: 12,
  },
  fiabilisation: {
    capitalizedPrices: 4820,
    analyzedOffers: 342,
    documentedCostEstimates: 47,
  },
  economy: {
    minAnnual: 3_000_000,
    maxAnnual: 4_500_000,
    operationsAudited: 18,
    operationsPerYear: 35,
    confidenceLevel: 'standard',
  },
  insights: [
    {
      type: 'fiabilite',
      subtype: 'capitalisation',
      title: 'Référentiel propriétaire consolidé',
      headlineValue: '4 820',
      caption: 'prix capitalisés sur 4 typologies',
      context: '12 mois d’utilisation continue',
      miniViz: {
        type: 'progression',
        unit: 'prix',
        data: [
          { label: 'M1', value: 340 },
          { label: 'M3', value: 1020 },
          { label: 'M6', value: 2180 },
          { label: 'M9', value: 3410 },
          { label: 'M12', value: 4820 },
        ],
      },
      footer: { label: 'AUTONOMIE DES ÉQUIPES', value: '8 économistes actifs', highlightColor: 'violet' },
    },
    {
      type: 'economie',
      subtype: 'bias',
      title: 'Biais systémique identifié',
      headlineValue: '18%',
      caption: 'sous-estimation moyenne lot Second Œuvre',
      context: 'observé sur 14 opérations sur 18',
      miniViz: {
        type: 'comparison',
        unit: 'k€',
        data: [
          { label: 'Op 1-5', value: -180 },
          { label: 'Op 6-10', value: -220 },
          { label: 'Op 11-14', value: -290, highlight: true },
          { label: 'Op 15-18', value: -240 },
        ],
      },
      footer: { label: 'POTENTIEL CAPTURABLE', value: '1,2 – 1,8 M€ / an', highlightColor: 'peach' },
    },
    {
      type: 'economie',
      subtype: 'dispersion',
      title: 'Dispersion inter-régions',
      headlineValue: '42%',
      caption: 'd’écart entre régions pour même typologie',
      context: 'lot gros œuvre logement collectif',
      miniViz: {
        type: 'bars',
        unit: '€/m²',
        data: [
          { label: 'IDF', value: 1450, highlight: true },
          { label: 'Lyon', value: 1180 },
          { label: 'Nantes', value: 1090 },
          { label: 'Lille', value: 1020 },
        ],
      },
      footer: { label: 'POTENTIEL CAPTURABLE', value: '1,8 – 2,7 M€ / an', highlightColor: 'peach' },
    },
  ],
  adoption: {
    pricesConsulted: 1847,
    anomaliesIdentified: 312,
    costEstimatesProduced: 47,
    activeUsers: 14,
    weeklyActivity: [
      { week: '2026-W05', pricesConsulted: 128 },
      { week: '2026-W06', pricesConsulted: 142 },
      { week: '2026-W07', pricesConsulted: 165 },
      { week: '2026-W08', pricesConsulted: 178 },
      { week: '2026-W09', pricesConsulted: 184 },
      { week: '2026-W10', pricesConsulted: 201 },
      { week: '2026-W11', pricesConsulted: 215 },
      { week: '2026-W12', pricesConsulted: 198 },
      { week: '2026-W13', pricesConsulted: 221 },
      { week: '2026-W14', pricesConsulted: 215 },
    ],
  },
  capitalization: {
    currentPriceCount: 4820,
    monthlyProgression: [
      { month: '2025-05', priceCount: 340 },
      { month: '2025-06', priceCount: 580 },
      { month: '2025-07', priceCount: 820 },
      { month: '2025-08', priceCount: 1020 },
      { month: '2025-09', priceCount: 1480 },
      { month: '2025-10', priceCount: 1890 },
      { month: '2025-11', priceCount: 2180 },
      { month: '2025-12', priceCount: 2740 },
      { month: '2026-01', priceCount: 3180 },
      { month: '2026-02', priceCount: 3620 },
      { month: '2026-03', priceCount: 4230 },
      { month: '2026-04', priceCount: 4820 },
    ],
  },
  scope: {
    dceCount: 18,
    periodStart: '2024-05',
    periodEnd: '2026-04',
    totalMarketValue: 87_300_000,
    lineItemsAnalyzed: 4820,
  },
};
